import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING = {
  role: "assistant",
  content:
    "Hi there! I'm Savvy Helper 👋 Tell me what tech trouble you're having — like a printer, Wi-Fi, or a new phone — and I'll point you the right way. I can even book a visit for you.",
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(
    (() => {
      let id = sessionStorage.getItem("tt-chat-session");
      if (!id) {
        id = (crypto.randomUUID && crypto.randomUUID()) || `s-${Date.now()}-${Math.random()}`;
        sessionStorage.setItem("tt-chat-session", id);
      }
      return id;
    })()
  );

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, sending]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId.current,
        message: text,
      });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Sorry, I had a little trouble just then. Please try again, or call us any time at (380) 282-2525.",
        },
      ]);
    } finally {
      setSending(false);
    }
  }, [input, sending]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="fixed bottom-6 left-6 z-[60] flex flex-col items-start gap-3 print:hidden">
      {open && (
        <div
          data-testid="chat-panel"
          className="w-[92vw] max-w-[380px] h-[540px] max-h-[75vh] rounded-2xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden animate-in"
          role="dialog"
          aria-label="Savvy Helper chat"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-3 px-5 py-4 bg-primary">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center h-10 w-10 rounded-full bg-accent/20 text-accent">
                <Sparkles className="h-5 w-5" />
              </span>
              <div>
                <p className="font-heading font-bold text-white leading-tight">Savvy Helper</p>
                <p className="text-xs text-slate-300">Friendly tech help · replies in seconds</p>
              </div>
            </div>
            <button
              data-testid="chat-close"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="grid place-items-center h-9 w-9 rounded-full text-slate-300 hover:bg-white/10"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-secondary">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-message-${m.role}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[15px] leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-accent text-white rounded-br-md"
                      : "bg-card border border-border text-foreground rounded-bl-md"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="chat-typing">
                <div className="rounded-2xl rounded-bl-md bg-card border border-border px-4 py-3">
                  <Loader2 className="h-4 w-4 animate-spin text-accent" />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-border bg-card">
            <div className="flex items-end gap-2">
              <textarea
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder="Type your question…"
                className="flex-1 resize-none max-h-28 rounded-2xl border border-border bg-secondary px-4 py-3 text-[15px] text-foreground outline-none focus:border-accent"
              />
              <button
                data-testid="chat-send"
                onClick={send}
                disabled={sending || !input.trim()}
                aria-label="Send message"
                className="grid place-items-center h-12 w-12 shrink-0 rounded-full bg-accent text-white hover:bg-[hsl(196,82%,27%)] transition-colors disabled:opacity-50"
              >
                <Send className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        data-testid="chat-launch"
        onClick={() => setOpen((v) => !v)}
        aria-label="Chat with Savvy Helper"
        aria-expanded={open}
        className="group inline-flex items-center gap-3 h-16 rounded-full bg-accent text-white shadow-2xl hover:bg-[hsl(196,82%,27%)] transition-all pl-5 pr-6"
      >
        {open ? <X className="h-7 w-7" /> : <MessageCircle className="h-7 w-7" />}
        {!open && <span className="font-bold text-lg hidden sm:inline">Ask Savvy Helper</span>}
      </button>
    </div>
  );
};
